/**
 * Acute-case desk: a right-side drawer on the ZUB calendar through which the
 * surgery and reception work the phone assistant's acute cases.
 *
 * Roles are enforced server-side (ordinaceProcedure / recepceProcedure) —
 * what this component does is decide which controls are worth showing.
 */

import { LogOut } from 'lucide-react'
import { useEffect } from 'react'
import { toast } from 'sonner'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import {
	Sheet,
	SheetBody,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet'
import { useDeskSession } from '@/hooks/useDeskSession'
import { CALENDAR_CONFIG } from '@/lib/constants'
import { trpc } from '@/trpc/client'
import { AcuteCaseDeskCard, type DeskAcuteCase } from './AcuteCaseDeskCard'
import { DeskLoginForm } from './DeskLoginForm'

const ROLE_LABELS = {
	ordinace: 'Ordinace',
	recepce: 'Recepce',
} as const

interface AcuteDeskDrawerProps {
	open: boolean
	onOpenChange: (open: boolean) => void
}

export function AcuteDeskDrawer({ open, onOpenChange }: AcuteDeskDrawerProps) {
	const utils = trpc.useUtils()
	// `open` doubles as the verify flag — the stored session is only checked
	// against the server when the drawer is actually being used.
	const desk = useDeskSession(open)
	const { refresh, handleAuthError, role } = desk

	// A session that expired while the tab sat open overnight must be noticed
	// before anything renders as logged in.
	useEffect(() => {
		if (open) refresh()
	}, [open, refresh])

	const listQuery = trpc.voiceAgent.desk.listAcute.useQuery(
		{ includeResolved: false },
		{
			enabled: open && role !== null,
			refetchOnWindowFocus: true,
			// Poll while the drawer is open. Without this the list is frozen at
			// the moment it was opened: on a terminal that never loses window
			// focus, reception would never see a note the surgery just wrote —
			// which is the whole point of the feature. Same cadence as the
			// calendar's own refresh.
			refetchInterval: CALENDAR_CONFIG.AUTO_REFRESH_INTERVAL,
		},
	)

	// The server is the authority on the session; a token it rejects is dead
	// regardless of what the locally cached expiry says. Gated on being logged
	// in so an error left over from the logged-out moment can't act on — and
	// immediately discard — a session that was just created.
	const listError = listQuery.error
	useEffect(() => {
		if (role && listError) handleAuthError(listError)
	}, [listError, role, handleAuthError])

	const invalidate = () => {
		utils.voiceAgent.desk.listAcute.invalidate()
		utils.voiceAgent.acuteCount.invalidate()
		// The /voice-agent page reads the same records
		utils.voiceAgent.list.invalidate()
		utils.voiceAgent.newCount.invalidate()
	}

	const addNote = trpc.voiceAgent.desk.addNote.useMutation({
		onSuccess: invalidate,
		onError: (err) => {
			desk.handleAuthError(err)
			toast.error(err.message || 'Poznámku se nepodařilo uložit.')
		},
	})

	const resolve = trpc.voiceAgent.desk.resolve.useMutation({
		onSuccess: () => {
			invalidate()
			toast.success('Případ označen jako vyřešený.')
		},
		onError: (err) => {
			desk.handleAuthError(err)
			toast.error(err.message || 'Případ se nepodařilo uzavřít.')
		},
	})

	const handleAddNote = async (caseId: string, text: string) => {
		try {
			await addNote.mutateAsync({ caseId, text })
			return true
		} catch {
			// Reported by onError; returning false keeps the draft in the textarea
			return false
		}
	}

	// Annotated, not cast: this way TypeScript checks the router's output
	// against the shape the card expects on every build, so a renamed or
	// dropped server field is a compile error rather than a blank field.
	const cases: DeskAcuteCase[] = listQuery.data?.cases ?? []

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent aria-describedby="acute-desk-description">
				<SheetHeader>
					<SheetTitle>Akutní případy</SheetTitle>
					<SheetDescription id="acute-desk-description">
						{role
							? 'Záznamy telefonního asistenta k vyřízení.'
							: 'Přihlaste se k zobrazení akutních případů.'}
					</SheetDescription>
					{role && (
						<div className="mt-3 flex items-center justify-between">
							<span className="rounded-full bg-[#f5ebe0] px-3 py-1 text-[12px] font-medium text-[#5c5142]">
								{ROLE_LABELS[role]}
							</span>
							<button
								type="button"
								onClick={desk.logout}
								className="inline-flex items-center gap-1.5 text-[13px] text-[#6b5c4c] transition-colors hover:text-[#3d3428] hover:underline"
							>
								<LogOut className="h-3.5 w-3.5" />
								Odhlásit
							</button>
						</div>
					)}
				</SheetHeader>

				<SheetBody>
					{!role ? (
						<DeskLoginForm
							onSubmit={desk.login}
							isPending={desk.isLoggingIn}
							error={desk.error}
						/>
					) : listQuery.error ? (
						// Never render a failed query as an empty list — an acute case
						// silently missing is the one failure mode that matters here.
						<div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-900">
							<p className="font-medium">Případy se nepodařilo načíst.</p>
							<p className="mt-1">{listQuery.error.message}</p>
							<button
								type="button"
								onClick={() => listQuery.refetch()}
								className="mt-3 inline-flex h-9 items-center rounded-lg border border-red-300 bg-white px-3 text-sm font-medium text-red-900 transition-colors hover:bg-red-100"
							>
								Zkusit znovu
							</button>
						</div>
					) : !listQuery.isSuccess ? (
						// Anything that isn't a settled success is still loading. Keyed
						// off isSuccess rather than isLoading because a query that has
						// been reset (role switch) has no data and isn't fetching yet —
						// rendering that as "no cases" would be a lie.
						<div className="flex justify-center py-10">
							<LoadingSpinner size="lg" />
						</div>
					) : cases.length === 0 ? (
						<p className="py-10 text-center text-sm text-[#8c8275]">
							Žádné nevyřízené akutní případy.
						</p>
					) : (
						<div className="space-y-4">
							{cases.map((item) => (
								<AcuteCaseDeskCard
									key={item.id}
									item={item}
									role={role}
									onAddNote={handleAddNote}
									onResolve={(caseId) => resolve.mutate({ caseId })}
									isSaving={
										addNote.isPending && addNote.variables?.caseId === item.id
									}
									isResolving={
										resolve.isPending && resolve.variables?.caseId === item.id
									}
								/>
							))}
						</div>
					)}
				</SheetBody>
			</SheetContent>
		</Sheet>
	)
}
